import { useMemo , useState} from 'react';
import { ArrowRight, Check, X, Eye, EyeOff } from 'lucide-react';
import ChoixRenderer from '../../components/VueQuestion/ChoixRenderer';
import SuffixeRenderer from '../../components/VueQuestion/SuffixeRenderer';
import { compareAnswers, optionsDeFiche, isJsonAnswer, isComplexAnswer, getTableElementConfig, shapeMatches, aiguilleJuste, jugerTrous, normalizeAnswer } from '../../utils/questionUtils';
import TableResponseElementEleve from '../../components/VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TableResponseElementEleve';
import NumberLineElementEleve from '../../components/VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/NumberLineElementEleve';
import PlacerPointsRenderer from '../../components/VueQuestion/renderers/PlacerPointsRenderer';
import ProgrammeConstructionRenderer from '../../components/VueQuestion/renderers/ProgrammeConstructionRenderer';
import HorlogeAiguillesRenderer from '../../components/VueQuestion/renderers/HorlogeAiguillesRenderer';
import MathText from '../../components/VueQuestion/MathText';
import Repere2DRenderer from '../../components/VueQuestion/renderers/Repere2DRenderer';

// Transforme une valeur brute en choix pour ChoixRenderer (QCM uniquement)
const toChoix = (value, imageUrl) => {
  if (imageUrl) return { type: 'image', config: { imageUrl } };
  if (typeof value === 'string' && value.startsWith('{')) {
    try { return JSON.parse(value); } catch { /* ignore */ }
  }
  return value;
};

// ─── Composants d'affichage correction par type ───

// Affichage correction Relier : paires avec ✅/❌
const CorrectionRelier = ({ reponseEleve, bonneReponse, light = false }) => {
  const { elevePairs, correctPairs } = useMemo(() => {
    try {
      return {
        elevePairs: JSON.parse(reponseEleve || '[]'),
        correctPairs: JSON.parse(bonneReponse || '[]'),
      };
    } catch { return { elevePairs: [], correctPairs: [] }; }
  }, [reponseEleve, bonneReponse]);

  // Map des bonnes réponses pour lookup rapide
  const correctMap = useMemo(() => new Map(correctPairs.map(([s, t]) => [s, t])), [correctPairs]);

  return (
    <div className="space-y-1.5">
      {elevePairs.map(([source, target], i) => {
        const isCorrect = correctMap.get(source) === target;
        return (
          <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isCorrect
              ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/30 bg-emerald-500/10'
              : light ? 'border-red-300 bg-red-50' : 'border-red-500/30 bg-red-500/10'
          }`}>
            {isCorrect
              ? <Check size={14} className={light ? 'text-emerald-600 flex-shrink-0' : 'text-emerald-400 flex-shrink-0'} />
              : <X size={14} className={light ? 'text-red-600 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
            }
            <span className={`text-sm font-medium ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700' : 'text-red-300')}`}>{source}</span>
            <ArrowRight size={12} className={light ? 'text-gray-400 flex-shrink-0' : 'text-slate-500 flex-shrink-0'} />
            <span className={`text-sm ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700 line-through' : 'text-red-300 line-through')}`}>{target}</span>
            {!isCorrect && (
              <span className={`text-sm ml-1 ${light ? 'text-emerald-600' : 'text-emerald-400'}`}>→ {correctMap.get(source)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Affichage correction Ordonner : liste numérotée avec vert/rouge
const CorrectionOrdonner = ({ reponseEleve, bonneReponse, light = false }) => {
  const { eleveItems, correctItems } = useMemo(() => {
    try {
      return {
        eleveItems: JSON.parse(reponseEleve || '[]'),
        correctItems: JSON.parse(bonneReponse || '[]'),
      };
    } catch { return { eleveItems: [], correctItems: [] }; }
  }, [reponseEleve, bonneReponse]);

  return (
    <div className="space-y-1.5">
      {eleveItems.map((item, i) => {
        const isCorrect = item === correctItems[i];
        return (
          <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isCorrect
              ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/30 bg-emerald-500/10'
              : light ? 'border-red-300 bg-red-50' : 'border-red-500/30 bg-red-500/10'
          }`}>
            <span className={`w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
              isCorrect ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
            }`}>{i + 1}</span>
            <span className={`text-sm font-medium ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700 line-through' : 'text-red-300 line-through')}`}>{item}</span>
            {!isCorrect && (
              <span className={`text-sm ml-1 ${light ? 'text-emerald-600' : 'text-emerald-400'}`}>→ {correctItems[i]}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Parse un choix QCM — string simple ou JSON stringify d'un objet élément
const parseChoix = (val) => {
  if (typeof val !== 'string') return val;
  try { const p = JSON.parse(val); if (typeof p === 'object' && p !== null && p.type) return p; } catch {}
  return val;
};

// Affichage correction QCM Multi : chaque choix vert/rouge + manquants
const CorrectionQCMMulti = ({ reponseEleve, bonneReponse, light = false }) => {
  const { eleveChoix, incorrects, manquants } = useMemo(() => {
    let eleve = [];
    let bonnes = [];
    try { eleve = JSON.parse(reponseEleve); } catch {}
    try { bonnes = JSON.parse(bonneReponse); } catch {}
    if (!Array.isArray(eleve)) eleve = [reponseEleve].filter(Boolean);
    if (!Array.isArray(bonnes)) bonnes = [bonneReponse].filter(Boolean);

    const bonnesSet = new Set(bonnes);
    const eleveSet = new Set(eleve);
    return {
      eleveChoix: eleve,
      incorrects: eleve.filter(v => !bonnesSet.has(v)),
      manquants: bonnes.filter(v => !eleveSet.has(v)),
    };
  }, [reponseEleve, bonneReponse]);

  return (
    <div className="space-y-1.5">
      {/* Choix cochés par l'élève */}
      {eleveChoix.map((choix, i) => {
        const isOk = !incorrects.includes(choix);
        const parsed = parseChoix(choix);
        return (
          <div key={i} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm ${
            isOk
              ? light ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300'
              : light ? 'bg-red-50 text-red-700' : 'bg-red-500/15 text-red-300'
          }`}>
            <span className="text-xs">{isOk ? '✓' : '✗'}</span>
            <ChoixRenderer choix={parsed} className="font-medium" light={light} />
          </div>
        );
      })}
      {/* Réponses manquantes */}
      {manquants.length > 0 && (
        <div className={`mt-2 text-xs ${light ? 'text-gray-500' : 'text-slate-400'}`}>
          <span className="font-medium">Non coché :</span>
          {manquants.map((m, i) => (
            <span key={i} className={`inline-flex items-center ml-1.5 px-2 py-0.5 rounded ${light ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300'}`}>
              <ChoixRenderer choix={parseChoix(m)} className="text-xs" light={light} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Affichage correction Texte à trous : phrase avec mots vert/rouge
// Deux modes selon le contenu :
//   - Phrase contenant du KaTeX ($...$) : reconstruit l'équation avec les valeurs
//     élève et la rend via MathText, puis affiche dessous une ligne récap des
//     trous (pilules vert/rouge). Évite l'affichage texte brut illisible des
//     fractions \dfrac{...}{...}.
//   - Phrase texte pur : rendu inline classique avec pilules sur les trous.
// Regex /\{\{([^{}]+)\}\}/g aligné sur extractTrouWords (exclut `{` dans la
// capture pour gérer la convention triple-accolade {{{X}}} des trous dans KaTeX).
// Chaque pilule suit la règle du score (jugerTrous, 2026-09-29) : casse,
// accents, virgule, tolérance de la fiche et trous sans ordre ; un trou faux
// d'un groupe montre la valeur restée libre, celle qu'il fallait écrire.
const SANS_OPTIONS = {};
const CorrectionTexteTrou = ({ reponseEleve, bonneReponse, light = false, options = SANS_OPTIONS }) => {
  const TROU_RE_SOURCE = '\\{\\{([^{}]+)\\}\\}';

  const extractWords = (str) => {
    const words = [];
    const re = new RegExp(TROU_RE_SOURCE, 'g');
    let m;
    while ((m = re.exec(str || '')) !== null) words.push(m[1].trim());
    return words;
  };

  const eleveWords = useMemo(() => extractWords(reponseEleve), [reponseEleve]);
  const correctWords = useMemo(() => extractWords(bonneReponse), [bonneReponse]);
  // Par trou : juste ou non, et l'indice du mot attendu qui lui revient.
  const verdicts = useMemo(
    () => jugerTrous(eleveWords.map(normalizeAnswer), correctWords.map(normalizeAnswer), options),
    [eleveWords, correctWords, options],
  );

  // Mode 1 : phrase contenant du KaTeX (présence d'un `$`)
  const hasMath = (bonneReponse || '').includes('$');

  // Reconstruit la phrase finale en remplaçant chaque {{...}} par la valeur élève.
  // Les triple-accolades {{{X}}} deviennent {valEleve} → KaTeX-compatible.
  const phraseRendue = useMemo(() => {
    if (!hasMath || !bonneReponse) return '';
    let idx = 0;
    return bonneReponse.replace(new RegExp(TROU_RE_SOURCE, 'g'), () => {
      const val = eleveWords[idx] ?? '___';
      idx++;
      return val;
    });
  }, [hasMath, bonneReponse, eleveWords]);

  // Mode 2 (fallback texte pur) : segments avec pilules inline
  const segments = useMemo(() => {
    if (hasMath || !reponseEleve || !bonneReponse) return [];
    const parts = [];
    const re = new RegExp(TROU_RE_SOURCE, 'g');
    let lastIndex = 0;
    let m, idx = 0;
    while ((m = re.exec(bonneReponse)) !== null) {
      if (m.index > lastIndex) parts.push({ type: 'text', content: bonneReponse.substring(lastIndex, m.index) });
      const v = verdicts[idx];
      const correct = v ? correctWords[v.attendu] : m[1].trim();
      const eleve = eleveWords[idx] || '';
      parts.push({ type: 'slot', correct, eleve, isCorrect: v ? v.juste : false });
      lastIndex = m.index + m[0].length;
      idx++;
    }
    if (lastIndex < bonneReponse.length) parts.push({ type: 'text', content: bonneReponse.substring(lastIndex) });
    return parts;
  }, [hasMath, reponseEleve, bonneReponse, eleveWords, correctWords, verdicts]);

  const slotCls = (isCorrect) => isCorrect
    ? (light ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300')
    : (light ? 'border-red-400 bg-red-50 text-red-700' : 'border-red-500/50 bg-red-500/15 text-red-300');

  if (hasMath) {
    return (
      <div className={`text-sm leading-8 ${light ? 'text-gray-800' : 'text-slate-200'}`}>
        {/* Équation rendue KaTeX avec les valeurs élève */}
        <MathText>{phraseRendue}</MathText>

        {/* Ligne récap des trous (vert/rouge + correction si faux) */}
        {eleveWords.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`text-xs ${light ? 'text-gray-500' : 'text-slate-400'}`}>Tes trous :</span>
            {eleveWords.map((eleve, i) => {
              const v = verdicts[i];
              const correct = v ? correctWords[v.attendu] : '';
              const isCorrect = v ? v.juste : false;
              return (
                <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${slotCls(isCorrect)}`}>
                  {eleve || '—'}
                  {!isCorrect && (
                    <span className={`${light ? 'text-emerald-600' : 'text-emerald-400'}`}>→ {correct}</span>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Fallback texte pur : pilules inline dans le texte
  return (
    <div className={`text-sm leading-8 ${light ? 'text-gray-800' : 'text-slate-200'}`}>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          <span key={i}>{seg.content}</span>
        ) : (
          <span key={i} className={`inline-block mx-1 px-2 py-0.5 rounded-lg border-2 font-medium ${slotCls(seg.isCorrect)}`}>
            {seg.eleve || '—'}
            {!seg.isCorrect && <span className={`ml-1 text-xs ${light ? 'text-emerald-600' : 'text-emerald-400'}`}>({seg.correct})</span>}
          </span>
        )
      )}
    </div>
  );
};

// Affichage correction Classer : ta réponse (vert/rouge) + la bonne réponse
const CorrectionClasser = ({ reponseEleve, bonneReponse, light = false }) => {
  const { eleveData, correctData } = useMemo(() => {
    try {
      return {
        eleveData: JSON.parse(reponseEleve || '{}'),
        correctData: JSON.parse(bonneReponse || '{}'),
      };
    } catch { return { eleveData: {}, correctData: {} }; }
  }, [reponseEleve, bonneReponse]);

  // Map inversée : item → catégorie correcte
  const correctCatMap = useMemo(() => {
    const map = {};
    Object.entries(correctData).forEach(([cat, items]) => {
      (items || []).forEach(item => { map[item] = cat; });
    });
    return map;
  }, [correctData]);

  // Grille de catégories avec items colorés
  const renderCategories = (data, showErrors) => (
    <div className="flex gap-2">
      {Object.entries(data).map(([catName, items], ci) => (
        <div key={ci} className={`flex-1 rounded-xl p-2.5 border ${light ? 'border-gray-200' : 'border-slate-600/50'}`}>
          <span className={`text-[11px] font-semibold block text-center mb-1.5 ${light ? 'text-gray-600' : 'text-slate-300'}`}>{catName}</span>
          <div className="flex flex-wrap gap-1 justify-center">
            {(items || []).map((item, ii) => {
              const isItemCorrect = correctCatMap[item] === catName;
              return (
                <span key={ii} className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                  showErrors
                    ? isItemCorrect
                      ? light ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                      : light ? 'bg-red-50 text-red-700 border-red-300' : 'bg-red-500/15 text-red-300 border-red-500/30'
                    : light ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                }`}>
                  {item}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return renderCategories(eleveData, true);
};

// Affichage correction Placer points : droite graduée (si numberline dispo)
// + liste vert/rouge. volet 'correction' → bonnes positions (PlacerPointsRenderer),
// volet 'reponse' → points de l'élève colorés selon exactitude.
const CorrectionPlacerPoints = ({ reponseEleve, bonneReponse, numberline = null, volet = 'reponse', light = false }) => {
  const { elevePoints, correctPoints } = useMemo(() => {
    try {
      return {
        elevePoints: JSON.parse(reponseEleve || '{}'),
        correctPoints: JSON.parse(bonneReponse || '{}'),
      };
    } catch { return { elevePoints: {}, correctPoints: {} }; }
  }, [reponseEleve, bonneReponse]);

  const parseNum = (v) => parseFloat(String(v).replace(',', '.'));
  const formatVal = (v) => String(v).replace('.', ',');

  // Droite graduée avec les points de l'élève (vert si bon, rouge sinon)
  const configEleve = useMemo(() => {
    if (!numberline) return null;
    const points = Object.entries(elevePoints)
      .map(([label, v]) => {
        const value = parseFloat(String(v).replace(',', '.'));
        if (Number.isNaN(value)) return null;
        const attendu = correctPoints[label];
        const juste = attendu !== undefined
          && Math.abs(value - parseFloat(String(attendu).replace(',', '.'))) < 0.01;
        return { value, label, showValue: true, color: juste ? '#22c55e' : '#ef4444' };
      })
      .filter(Boolean);
    return { ...numberline, points, showArrow: true, showStart: true, showEnd: true };
  }, [numberline, elevePoints, correctPoints]);

  return (
    <div className="space-y-2">
      {/* Figure — fallback texte seul si numberline absente (vieilles données) */}
      {numberline ? (
        volet === 'correction'
          ? <PlacerPointsRenderer numberline={numberline} bonneReponse={bonneReponse} light={light} />
          : configEleve && configEleve.points.length > 0 && (
              <NumberLineElementEleve config={configEleve} theme={light ? 'light' : 'dark'} />
            )
      ) : null}

      <div className="space-y-1.5">
      {Object.entries(correctPoints).map(([label, correctVal]) => {
        const eleveVal = elevePoints[label];
        const isCorrect = eleveVal !== undefined && Math.abs(parseNum(eleveVal) - parseNum(correctVal)) < 0.01;
        return (
          <div key={label} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isCorrect
              ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/30 bg-emerald-500/10'
              : light ? 'border-red-300 bg-red-50' : 'border-red-500/30 bg-red-500/10'
          }`}>
            {isCorrect
              ? <Check size={14} className={light ? 'text-emerald-600 flex-shrink-0' : 'text-emerald-400 flex-shrink-0'} />
              : <X size={14} className={light ? 'text-red-600 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
            }
            <span className={`text-sm font-bold flex-shrink-0 ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700' : 'text-red-300')}`}>
              {label}
            </span>
            <span className={`text-sm ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700' : 'text-red-300')}`}>
              = {eleveVal !== undefined ? formatVal(eleveVal) : '—'}
            </span>
            {!isCorrect && (
              <span className={`text-sm ml-1 ${light ? 'text-emerald-600' : 'text-emerald-400'}`}>
                (attendu : {formatVal(correctVal)})
              </span>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};
// Affichage correction Placer des points 2D (repère orthogonal) : figure avec les
// points de l'élève (vert si bon, rouge sinon) ou la solution (volet 'correction'),
// + liste des verdicts par point. Valeurs canoniques "x;y" (virgule FR).
const CorrectionPlacerPoints2D = ({ reponseEleve, bonneReponse, repere = null, dejaPlaces = [], decor = null, relier = null, volet = 'reponse', light = false }) => {
  const { elevePoints, correctPoints } = useMemo(() => {
    try {
      return {
        elevePoints: JSON.parse(reponseEleve || '{}'),
        correctPoints: JSON.parse(bonneReponse || '{}'),
      };
    } catch { return { elevePoints: {}, correctPoints: {} }; }
  }, [reponseEleve, bonneReponse]);

  const parseXY = (v) => {
    const parts = String(v).split(';');
    if (parts.length !== 2) return null;
    const x = parseFloat(parts[0].replace(',', '.'));
    const y = parseFloat(parts[1].replace(',', '.'));
    return (Number.isNaN(x) || Number.isNaN(y)) ? null : { x, y };
  };
  const sameXY = (a, b) => a && b && Math.abs(a.x - b.x) < 0.001 && Math.abs(a.y - b.y) < 0.001;
  const formatXY = (v) => {
    const xy = parseXY(v);
    return xy ? `(${String(v).replace(';', ' ; ')})` : String(v);
  };

  // Points de l'élève colorés par verdict (volet 'reponse')
  const pointsEleve = useMemo(() => (
    Object.entries(elevePoints)
      .map(([label, v]) => {
        const xy = parseXY(v);
        if (!xy) return null;
        const juste = sameXY(xy, parseXY(correctPoints[label]));
        return { ...xy, label, color: juste ? '#22c55e' : '#ef4444' };
      })
      .filter(Boolean)
  ), [elevePoints, correctPoints]);

  return (
    <div className="space-y-2">
      {/* Figure — fallback liste seule si repère absent (vieilles données) */}
      {repere ? (
        volet === 'correction'
          ? <Repere2DRenderer repere={repere} bonneReponse={bonneReponse} dejaPlaces={dejaPlaces} decor={decor} relier={relier} light={light} titre={null} />
          : pointsEleve.length > 0 && (
              <Repere2DRenderer repere={repere} points={pointsEleve} dejaPlaces={dejaPlaces} decor={decor} relier={relier} light={light} titre={null} />
            )
      ) : null}

      <div className="space-y-1.5">
      {Object.entries(correctPoints).map(([label, correctVal]) => {
        const eleveVal = elevePoints[label];
        const isCorrect = eleveVal !== undefined && sameXY(parseXY(eleveVal), parseXY(correctVal));
        return (
          <div key={label} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isCorrect
              ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/30 bg-emerald-500/10'
              : light ? 'border-red-300 bg-red-50' : 'border-red-500/30 bg-red-500/10'
          }`}>
            {isCorrect
              ? <Check size={14} className={light ? 'text-emerald-600 flex-shrink-0' : 'text-emerald-400 flex-shrink-0'} />
              : <X size={14} className={light ? 'text-red-600 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
            }
            <span className={`text-sm font-bold flex-shrink-0 ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700' : 'text-red-300')}`}>
              {label}
            </span>
            <span className={`text-sm ${isCorrect ? (light ? 'text-emerald-700' : 'text-emerald-300') : (light ? 'text-red-700' : 'text-red-300')}`}>
              {eleveVal !== undefined ? formatXY(eleveVal) : '—'}
            </span>
            {!isCorrect && (
              <span className={`text-sm ml-1 ${light ? 'text-emerald-600' : 'text-emerald-400'}`}>
                (attendu : {formatXY(correctVal)})
              </span>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

// Affichage correction Programme de construction : figure (si contenu dispo)
// + liste des verdicts par tâche. volet 'reponse' → construction de l'élève
// (snap-on-success : ses shapes validées sont exactes), volet 'correction' → solution.
const CorrectionProgrammeConstruction = ({ reponseEleve, bonneReponse, tasks = [], tolerance = 12, pxPerCm = 30, viewBox = null, points = null, preShapes = null, light = false }) => {
  const { eleveShapes, correctShapes } = useMemo(() => {
    try {
      return {
        eleveShapes: JSON.parse(reponseEleve || '{}'),
        correctShapes: JSON.parse(bonneReponse || '{}'),
      };
    } catch { return { eleveShapes: {}, correctShapes: {} }; }
  }, [reponseEleve, bonneReponse]);

  // La réponse élève d'un angle_ray = { type:'ray', angleDeg } SANS `from` (le sommet,
  // retiré des tasks pour ne pas divulguer la solution). On le réinjecte depuis la bonne
  // réponse pour que le renderer puisse tracer le rayon depuis le bon sommet.
  const reponseElevePatched = useMemo(() => {
    try {
      const s = JSON.parse(reponseEleve || '{}');
      let changed = false;
      Object.keys(s).forEach((id) => {
        const sh = s[id]; const cor = correctShapes[id];
        if (sh && sh.type === 'ray' && sh.from == null && cor) {
          s[id] = { ...sh, from: cor.from, labelEnd: sh.labelEnd ?? cor.labelEnd };
          changed = true;
        }
      });
      return changed ? JSON.stringify(s) : reponseEleve;
    } catch { return reponseEleve; }
  }, [reponseEleve, correctShapes]);

  const formatCm = (px) => {
    const cm = px / pxPerCm;
    return (Number.isInteger(cm) ? String(cm) : cm.toFixed(1).replace('.', ','));
  };

  // Si pas de liste de tasks (cas correction serveur sans contexte), on itère sur les clés
  const taskIds = tasks.length > 0 ? tasks.map(t => t.id) : Object.keys(correctShapes);

  return (
    <div className="space-y-2">
      {/* Figure — le renderer dessine le jeu de shapes qu'on lui passe :
          la réponse de l'élève ici, la solution côté volet correction.
          Fallback liste seule si viewBox absent (vieilles données). */}
      {viewBox ? (
        <ProgrammeConstructionRenderer
          viewBox={viewBox}
          points={points || []}
          preShapes={preShapes || []}
          tasks={tasks}
          pxPerCm={pxPerCm}
          bonneReponse={reponseElevePatched}
          light={light}
          showConsignes={false}
        />
      ) : null}

      <div className="space-y-1.5">
      {taskIds.map((id) => {
        const exp = correctShapes[id];
        const got = eleveShapes[id];
        const task = tasks.find(t => t.id === id);
        // Même juge géométrique que le scoring (questionUtils.shapeMatches) —
        // le verdict affiché et le score ne peuvent plus diverger.
        const isCorrect = shapeMatches(got, exp, tolerance);

        const status = isCorrect ? 'ok' : got ? 'wrong' : 'missing';

        return (
          <div key={id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            status === 'ok'
              ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/30 bg-emerald-500/10'
              : status === 'wrong'
                ? light ? 'border-red-300 bg-red-50' : 'border-red-500/30 bg-red-500/10'
                : light ? 'border-gray-300 bg-gray-50' : 'border-slate-600/30 bg-slate-600/10'
          }`}>
            {status === 'ok'
              ? <Check size={14} className={light ? 'text-emerald-600 flex-shrink-0' : 'text-emerald-400 flex-shrink-0'} />
              : status === 'wrong'
                ? <X size={14} className={light ? 'text-red-600 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
                : <span className={`text-xs flex-shrink-0 ${light ? 'text-gray-500' : 'text-slate-400'}`}>⏭</span>
            }
            {task?.color && (
              <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: task.color }} />
            )}
            <span className={`text-sm font-bold flex-shrink-0 ${
              status === 'ok' ? (light ? 'text-emerald-700' : 'text-emerald-300')
              : status === 'wrong' ? (light ? 'text-red-700' : 'text-red-300')
              : (light ? 'text-gray-600' : 'text-slate-400')
            }`}>{id}</span>
            <span className={`text-xs sm:text-sm flex-1 ${
              status === 'ok' ? (light ? 'text-emerald-700' : 'text-emerald-300')
              : status === 'wrong' ? (light ? 'text-red-700' : 'text-red-300')
              : (light ? 'text-gray-500 italic' : 'text-slate-400 italic')
            }`}>
              {(() => {
                if (status === 'missing') return 'non tracé';
                if (exp?.type === 'line') {
                  return isCorrect ? 'droite correcte' : 'droite incorrecte';
                }
                if (exp?.type === 'ray') {
                  if (isCorrect) return 'angle correct';
                  return `angle ${Math.round(got.angleDeg)}° (attendu ${Math.round(exp.angleDeg)}°)`;
                }
                return `r = ${formatCm(got.r)} cm${!isCorrect && exp ? ` (attendu ${formatCm(exp.r)} cm)` : ''}`;
              })()}
            </span>
          </div>
        );
      })}
      </div>
    </div>
  );
};

// ─── Composant principal ───

/**
 * @param {boolean} allowFlip - si true, icône Eye pour flipper vers la correction (quand incorrect)
 */

/**
 * Correction « placer_aiguilles » : l'horloge (angles de l'eleve ou solution)
 * + verdicts PAR AIGUILLE via questionUtils.aiguilleJuste — le meme juge que
 * le scoring, les verdicts affiches ne peuvent pas diverger du score.
 */
const NOMS_AIGUILLES = { heures: 'Petite aiguille (heures)', minutes: 'Grande aiguille (minutes)', secondes: 'Trotteuse (secondes)' };
const CorrectionPlacerAiguilles = ({ reponseEleve, bonneReponse, avecTrotteuse, volet, light }) => {
  let cible = null;
  try { cible = JSON.parse(bonneReponse).aiguilles; } catch { /* verdicts masques */ }
  let angles = null;
  try { angles = JSON.parse(reponseEleve); } catch { /* pas de reponse lisible */ }
  const estSolution = volet === 'correction';
  const mains = ['heures', 'minutes', ...((cible && cible.sec != null) || avecTrotteuse ? ['secondes'] : [])];
  return (
    <div className="space-y-2">
      <HorlogeAiguillesRenderer reponse={estSolution ? bonneReponse : reponseEleve} avecTrotteuse={avecTrotteuse} light={light} />
      {!estSolution && cible && (
        <div className="space-y-1.5">
          {mains.map((m) => {
            const ok = aiguilleJuste(m, angles ? angles[m] : null, cible);
            return (
              <div key={m} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                ok
                  ? light ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                  : light ? 'border-red-300 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'
              }`}>
                <span className="flex-1">{NOMS_AIGUILLES[m]}</span>
                <span>{ok ? '✓' : '✗'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function CorrectionItem({ itemId, ItemBilanDetail, testSpecifique, typeQuestion, subType, suffixe, contenu = null, allowFlip = false, theme = 'dark' }) {
  // State flip interne — appelé AVANT le guard pour respecter les rules of hooks
  const [internalFlip, setInternalFlip] = useState(false);

  const reponseData = ItemBilanDetail?.reponses[testSpecifique]?.[itemId];
  if (!reponseData) return null;

  // contenu de la question (numberline, viewBox, points, preShapes…) — sert aux
  // corrections visuelles placer_points / programme_construction. Parse défensif.
  let contenuObj = contenu;
  if (typeof contenu === 'string') {
    try { contenuObj = JSON.parse(contenu); } catch { contenuObj = null; }
  }

  const reponseEleve = reponseData.réponse || '';
  const bonneReponse = reponseData.bonne_reponse || '';
  const bonneReponseImage = reponseData.bonne_reponse_image;
  const imageEleve = reponseData.image;
  const light = theme === 'light';

  // `tolerant` embarqué dans la fiche réponse à la passation (Quiz.jsx) ;
  // anciennes données sans le champ → undefined → comparaison exacte (historique).
  const isCorrect = compareAnswers(reponseEleve, bonneReponse, bonneReponseImage, optionsDeFiche(reponseData));


  // Détection du type : priorité à typeQuestion + subType (props), fallback sur sniffing
  const isTableau = isComplexAnswer(bonneReponse);
  const isRelier = subType === 'relier' || (!subType && !typeQuestion && isJsonAnswer(reponseEleve) && (() => { try { const p = JSON.parse(reponseEleve); return Array.isArray(p) && Array.isArray(p[0]); } catch { return false; } })());
  const isOrdonner = subType === 'ordonner' || (!subType && !typeQuestion && isJsonAnswer(reponseEleve) && (() => { try { const p = JSON.parse(reponseEleve); return Array.isArray(p) && !Array.isArray(p[0]); } catch { return false; } })());
  const isTexteTrou = subType === 'texte_trou' || (!subType && !typeQuestion && reponseEleve.includes('{{') && bonneReponse.includes('{{'));
  const isClasser = subType === 'classer';
  const isPlacerPoints = subType === 'placer_points';
  const isPlacerPoints2D = subType === 'placer_points_2d';
  const isProgrammeConstruction = subType === 'programme_construction';
  const isPlacerAiguilles = subType === 'placer_aiguilles';
  const isMulti = typeQuestion === 'qcm_multiple';
  const isAssociation = isRelier || isOrdonner || isClasser || isTexteTrou || isPlacerPoints || isPlacerPoints2D || isProgrammeConstruction || isPlacerAiguilles;

  const tableauConfig = isTableau ? getTableElementConfig({ bonne_reponse: bonneReponse }) : null;

  const reponseChoix = toChoix(reponseEleve, imageEleve);
  const bonneReponseChoix = toChoix(bonneReponse, bonneReponseImage);

  // ─── Rendu de la réponse élève ───
  const renderReponseEleve = () => {
    if (!reponseEleve) return <p className={`italic text-sm ${light ? 'text-gray-400' : 'text-slate-500'}`}>Pas de réponse</p>;
    if (isMulti) return <CorrectionQCMMulti reponseEleve={reponseEleve} bonneReponse={bonneReponse} light={light} />;
    if (isTableau && tableauConfig) return <TableResponseElementEleve config={tableauConfig} reponseExistante={reponseEleve} readOnly light={light} compact={light} />;
    if (isAssociation) {
      if (isRelier) return <CorrectionRelier reponseEleve={reponseEleve} bonneReponse={bonneReponse} light={light} />;
      if (isOrdonner) return <CorrectionOrdonner reponseEleve={reponseEleve} bonneReponse={bonneReponse} light={light} />;
      if (isTexteTrou) return <CorrectionTexteTrou reponseEleve={reponseEleve} bonneReponse={bonneReponse} light={light} options={optionsDeFiche(reponseData)} />;
      if (isClasser) return <CorrectionClasser reponseEleve={reponseEleve} bonneReponse={bonneReponse} isCorrect={isCorrect} light={light} />;
      if (isPlacerPoints) return <CorrectionPlacerPoints reponseEleve={reponseEleve} bonneReponse={bonneReponse} numberline={contenuObj?.numberline} volet="reponse" light={light} />;
      if (isPlacerPoints2D) return <CorrectionPlacerPoints2D reponseEleve={reponseEleve} bonneReponse={bonneReponse} repere={contenuObj?.repere} dejaPlaces={contenuObj?.dejaPlaces || []} decor={contenuObj?.decor || null} relier={contenuObj?.relier || null} volet="reponse" light={light} />;
      if (isProgrammeConstruction) return <CorrectionProgrammeConstruction reponseEleve={reponseEleve} bonneReponse={bonneReponse} tasks={reponseData?.tasks || contenuObj?.tasks || []} tolerance={reponseData?.tolerance ?? contenuObj?.tolerance} pxPerCm={reponseData?.pxPerCm ?? contenuObj?.pxPerCm} viewBox={contenuObj?.viewBox} points={contenuObj?.points} preShapes={contenuObj?.preShapes} light={light} />;
      if (isPlacerAiguilles) return <CorrectionPlacerAiguilles reponseEleve={reponseEleve} bonneReponse={bonneReponse} avecTrotteuse={!!contenuObj?.avecTrotteuse} volet="reponse" light={light} />;
    }
    return (
      <span className="flex items-center gap-1">
        <ChoixRenderer choix={reponseChoix} className={`font-medium ${light ? 'text-gray-800' : 'text-white'}`} compact />
        {typeQuestion === 'texte' && suffixe && <SuffixeRenderer suffixe={suffixe} className={`text-sm ${light ? 'text-gray-500' : 'text-slate-400'}`} />}
      </span>
    );
  };

  // ─── Rendu de la bonne réponse ───
  const renderBonneReponse = () => {
    if (isMulti) return <CorrectionQCMMulti reponseEleve={bonneReponse} bonneReponse={bonneReponse} light={light} />;
    if (isTableau && tableauConfig) return <TableResponseElementEleve config={tableauConfig} reponseExistante={bonneReponse} readOnly light={light} compact={light} />;
    if (isAssociation) {
      if (isRelier) return <CorrectionRelier reponseEleve={bonneReponse} bonneReponse={bonneReponse} light={light} />;
      if (isOrdonner) return <CorrectionOrdonner reponseEleve={bonneReponse} bonneReponse={bonneReponse} light={light} />;
      if (isTexteTrou) return <CorrectionTexteTrou reponseEleve={bonneReponse} bonneReponse={bonneReponse} light={light} />;
      if (isClasser) return <CorrectionClasser reponseEleve={bonneReponse} bonneReponse={bonneReponse} isCorrect={true} light={light} />;
      if (isPlacerPoints) return <CorrectionPlacerPoints reponseEleve={bonneReponse} bonneReponse={bonneReponse} numberline={contenuObj?.numberline} volet="correction" light={light} />;
      if (isPlacerPoints2D) return <CorrectionPlacerPoints2D reponseEleve={bonneReponse} bonneReponse={bonneReponse} repere={contenuObj?.repere} dejaPlaces={contenuObj?.dejaPlaces || []} decor={contenuObj?.decor || null} relier={contenuObj?.relier || null} volet="correction" light={light} />;
      if (isProgrammeConstruction) return <CorrectionProgrammeConstruction reponseEleve={bonneReponse} bonneReponse={bonneReponse} tasks={reponseData?.tasks || contenuObj?.tasks || []} tolerance={reponseData?.tolerance ?? contenuObj?.tolerance} pxPerCm={reponseData?.pxPerCm ?? contenuObj?.pxPerCm} viewBox={contenuObj?.viewBox} points={contenuObj?.points} preShapes={contenuObj?.preShapes} light={light} />;
      if (isPlacerAiguilles) return <CorrectionPlacerAiguilles reponseEleve={bonneReponse} bonneReponse={bonneReponse} avecTrotteuse={!!contenuObj?.avecTrotteuse} volet="correction" light={light} />;
    }
    return (
      <span className="flex items-center gap-1">
        <ChoixRenderer choix={bonneReponseChoix} className={`font-medium ${light ? 'text-gray-800' : 'text-white'}`} compact />
        {typeQuestion === 'texte' && suffixe && <SuffixeRenderer suffixe={suffixe} className={`text-sm ${light ? 'text-gray-500' : 'text-slate-400'}`} />}
      </span>
    );
  };

  // ─── Mode autonome : allowFlip — icône Eye en haut à droite ───
  if (allowFlip) {
    const currentView = internalFlip ? 'correction' : 'reponse';

    const renderContent = () => {
      if (currentView === 'reponse') {
        return light ? (
          <div className={`relative rounded-xl p-4 border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            {!isCorrect && (
              <button onClick={() => setInternalFlip(true)} className="absolute top-0 right-0 p-2.5 rounded-bl-xl rounded-tr-xl border-l border-b border-emerald-600 text-emerald-600 hover:text-white hover:bg-emerald-600 transition-all" title="Voir le corrigé">
                <Eye size={18} />
              </button>
            )}
            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`}>
              Réponse donnée {isCorrect ? '— Correct' : '— Incorrect'}
            </p>
            <div>{isCorrect ? renderBonneReponse() : renderReponseEleve()}</div>
          </div>
        ) : (
          isCorrect ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🎉</span>
                <div>
                  <p className="text-emerald-400 font-bold">Bravo, bonne réponse !</p>
                  <p className="text-emerald-300/70 text-sm">Tu as trouvé</p>
                </div>
              </div>
              <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20 mt-3">
                {renderBonneReponse()}
              </div>
            </div>
          ) : (
            <div className="relative bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <button onClick={() => setInternalFlip(true)} className="absolute top-0 right-0 p-2.5 rounded-bl-xl rounded-tr-xl border-l border-b border-emerald-600 text-emerald-600 hover:text-white hover:bg-emerald-600 transition-all" title="Voir le corrigé">
                <Eye size={18} />
              </button>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">😕</span>
                <div>
                  <p className="text-red-400 font-bold">Pas tout à fait...</p>
                  <p className="text-red-300/70 text-sm">Voici ce que tu as répondu</p>
                </div>
              </div>
              <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                {renderReponseEleve()}
              </div>
            </div>
          )
        );
      }
      // correction
      return light ? (
        <div className="relative rounded-xl p-4 border bg-emerald-50 border-emerald-200">
          <button onClick={() => setInternalFlip(false)} className="absolute top-0 right-0 p-2.5 rounded-bl-xl rounded-tr-xl border-l border-b border-amber-600 text-amber-600 hover:text-white hover:bg-amber-600 transition-all" title="Revoir la réponse">
            <EyeOff size={18} />
          </button>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-emerald-600">Bonne réponse</p>
          <div>{renderBonneReponse()}</div>
        </div>
      ) : (
        <div className="relative bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <button onClick={() => setInternalFlip(false)} className="absolute top-0 right-0 p-2.5 rounded-bl-xl rounded-tr-xl border-l border-b border-amber-600 text-amber-600 hover:text-white hover:bg-amber-600 transition-all" title="Revoir la réponse">
            <EyeOff size={18} />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-xl">✅</span>
            </div>
            <div>
              <p className="text-emerald-400 font-bold">La bonne réponse</p>
              <p className="text-emerald-300/70 text-sm">C'était celle-ci</p>
            </div>
          </div>
          <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
            {renderBonneReponse()}
          </div>
        </div>
      );
    };

    return <div key={currentView} style={{ animation: 'fadeInUp 0.2s ease-out' }}>{renderContent()}</div>;
  }

  // ─── Pas de flip (correct ou allowFlip=false) — affiche la réponse directement ───
  return (
    <div>
      {light ? (
        <div className={`rounded-xl p-4 border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`}>
            Réponse donnée {isCorrect ? '— Correct' : '— Incorrect'}
          </p>
          <div>{isCorrect ? renderBonneReponse() : renderReponseEleve()}</div>
        </div>
      ) : (
        isCorrect ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <p className="text-emerald-400 font-bold">Bravo, bonne réponse !</p>
                <p className="text-emerald-300/70 text-sm">Tu as trouvé</p>
              </div>
            </div>
            <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20 mt-3">
              {renderBonneReponse()}
            </div>
          </div>
        ) : (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">😕</span>
              <div>
                <p className="text-red-400 font-bold">Pas tout à fait...</p>
                <p className="text-red-300/70 text-sm">Voici ce que tu as répondu</p>
              </div>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
              {renderReponseEleve()}
            </div>
          </div>
        )
      )}
    </div>
  );
}

export default CorrectionItem;
export { CorrectionRelier, CorrectionOrdonner, CorrectionTexteTrou, CorrectionClasser, CorrectionQCMMulti };
